// #ux-redesign-start: passkeyService (Auth redesign · WebAuthn · Patients)
//
// Cliente del flow WebAuthn contra MS-Auth (PatientsPasskeyController). El
// ceremonial WebAuthn vive en el browser (navigator.credentials.{create,get})
// y este service sólo intermedia los 2 round-trips begin/finish con el back.
//
// Reusa `AuthService` (singleton) cuyo baseUrl ya es `<api>/auth/patients`
// (lo setea usersSaga.js con AuthService.setBaseUrl("/patients")) y maneja
// setToken/getToken para sesiones.
// ─────────────────────────────────────────────────────────────────────────────

import AuthService from 'plugins/TS-lib-utils-public/services/AuthService';

// ── helpers base64url <-> ArrayBuffer ───────────────────────────────────────
export const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const b64uToBuf = (b64u) => {
  const b64 = (b64u + '==='.slice((b64u.length + 3) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};

export const bufToB64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

// Estandariza la forma en que extraemos status + data + error de los
// resultados de AxiosService (que swallowea errores y devuelve un objeto
// con `response`).
const unwrap = (result) => {
  if (result && result.status === 200) return { ok: true, data: result.data };
  if (result && result.response) {
    return {
      ok: false,
      status: result.response.status,
      data: result.response.data,
    };
  }
  return { ok: false, status: 0, data: 'Network error' };
};

// rpId derivado del browser. WebAuthn requiere hostname (no IP, no port).
const currentRpId = () => window.location.hostname;

// ── BEGIN / FINISH endpoints ───────────────────────────────────────────────
export const passkeyService = {
  registerBegin: async ({ email }) => {
    const result = await AuthService.post('/passkey/register/begin', {
      email,
      rpId: currentRpId(),
    });
    return unwrap(result);
  },

  registerFinish: async (body) => {
    const result = await AuthService.post('/passkey/register/finish', body);
    if (result && result.status === 200 && result.data && result.data.token) {
      // #ux-redesign: registerFinish ahora emite sesión (enrolar + entrar en un
      // paso). Persistimos el token igual que authFinish.
      AuthService.setToken(result.data.token);
    }
    return unwrap(result);
  },

  authBegin: async ({ email }) => {
    const result = await AuthService.post('/passkey/auth/begin', {
      email,
      rpId: currentRpId(),
    });
    return unwrap(result);
  },

  authFinish: async (body) => {
    const result = await AuthService.post('/passkey/auth/finish', body);
    if (result && result.status === 200 && result.data && result.data.token) {
      // Mismo patrón que AuthService.login: persistir el token para que las
      // siguientes requests autenticadas lo manden en el Authorization.
      AuthService.setToken(result.data.token);
    }
    return unwrap(result);
  },

  // Gestión de passkeys del user logueado (Account > Security).
  list: async () => {
    const result = await AuthService.get('/passkey');
    return unwrap(result);
  },

  remove: async (credentialId) => {
    const result = await AuthService.delete(
      `/passkey/${encodeURIComponent(credentialId)}`
    );
    return unwrap(result);
  },
};

export default passkeyService;
// #ux-redesign-end
